// One presentation model for the Checks, Preview and Result steps of the report analysis page.
// It only rearranges what the backend returned:
//   validation  POST report/validate-files (files, errors, warnings, acknowledgements, token)
//   preview     POST report/analyze-files/preview (analysisSummary, rows, conflicts, stale, periodSummary)
//   result      POST report/analyze-files (the saved run's summary)
//   error       a refused request ({ code, status, phase, details })
// Nothing is calculated here except counts of items and sums of per-file row counts.

const SOURCE_PERIOD = "SOURCE_PERIOD_DIFFERS_FROM_SELECTED";
const DAILY_PERIOD = "ROWS_OUTSIDE_SELECTED_PERIOD";
export const CROSS_STORE = "CROSS_STORE_BRANCH_MISMATCH";
// Warnings that need an explicit acknowledgement: shown once, from validation.acknowledgements (and
// after the checks from the preview / saved run), never again from the file's own warning list.
const ACKNOWLEDGEABLE = new Set([SOURCE_PERIOD, DAILY_PERIOD, CROSS_STORE]);
// A save that failed with one of these may be tried again with the same files: the backend answers
// an identical, already committed request with the earlier result (no second save).
export const RETRYABLE = new Set(["NETWORK_ERROR", "TRANSACTION_UNAVAILABLE", "COMMIT_FAILED", "COMMIT_OUTCOME_UNKNOWN", "UPLOAD_FAILED", "RATE_LIMITED", "ANALYSIS_FAILED", "DOWNLOAD_FAILED"]);

const issue = (item, extra = {}) => ({ ...item, ...extra });
const knownWarning = (warning, isKnown) => !ACKNOWLEDGEABLE.has(warning.code) && isKnown(warning.code);

// files: one entry per uploaded file, from the most complete source available. Before any check
// (planned: submission().files) only what the page itself knows: names, sections, add/replace.
function filesOf({ validation, preview, result }, planned = []) {
  const summaryFiles = preview?.analysisSummary?.files || result?.analysisSummary?.files || null;
  const checked = validation?.files || [];
  if (!checked.length && !summaryFiles && !(preview?.files || result?.files || []).length) {
    return planned.map((file) => ({
      fileName: file.name || null, app: file.app, section: file.section, status: null, sourceRows: null, sourcePeriods: [], branches: [],
      operation: file.operation, noop: Boolean(file.keep), fromVersion: file.expectedVersion ?? null, fileVersion: file.operation === "replace" && !file.keep ? file.expectedVersion + 1 : file.expectedVersion ?? null,
      errors: [], warningCodes: [], warnings: [],
    }));
  }
  const count = Math.max(checked.length, summaryFiles?.length || 0, (preview?.files || result?.files || []).length);
  return Array.from({ length: count }, (_, index) => {
    const fromCheck = checked[index] || {};
    const fromSummary = summaryFiles?.[index] || {};
    const fromSlots = (preview?.files || result?.files || [])[index] || {};
    return {
      fileName: fromCheck.fileName || fromSummary.fileName || fromSlots.fileName || null,
      app: fromCheck.app || fromSummary.app || fromSlots.app || null,
      section: fromCheck.section || fromSummary.section || fromSlots.section || null,
      status: fromCheck.status || fromSummary.status || null,
      sourceRows: fromCheck.evidence?.rows ?? fromSummary.sourceRows ?? null,
      // Row accounting computed by the backend (included / excluded and why); never derived here.
      accounting: fromCheck.accounting || fromSummary.accounting || null,
      sourcePeriods: fromCheck.evidence?.sourcePeriods || fromSummary.sourcePeriods || [],
      branches: fromCheck.evidence?.branches || [],
      operation: fromSummary.operation || fromSlots.operation || (fromCheck.partKey ? "replace" : "add"),
      noop: Boolean(fromSummary.noop ?? fromSlots.noop),
      fromVersion: fromSummary.fromVersion ?? fromSlots.fromVersion ?? null,
      fileVersion: fromSummary.fileVersion ?? fromSlots.fileVersion ?? null,
      errors: fromCheck.errors || [],
      warningCodes: fromCheck.warnings ? fromCheck.warnings.map((warning) => warning.code) : fromSummary.warnings || [],
      warnings: fromCheck.warnings || (fromSummary.warnings || []).map((code) => ({ code })),
    };
  });
}

// Problems found before anything is sent (reportAnalysisModel.submission().blockers) as review
// issues: blocking ones are errors, `warningOnly` ones are warnings.
const blockerIssue = (blocker) => ({
  code: blocker.code,
  app: blocker.app,
  fileName: blocker.name || blocker.part?.currentFile?.uploadedName || blocker.part?.currentFile?.originalName || null,
  ...(blocker.count ? { count: blocker.count } : {}),
});

// state: the page reducer state; isKnownWarning(code): the warning has a user-facing text.
// blockers: submission().blockers; planned: files the check would send; maxFiles: upload limit.
export function buildReview(state, { storeName = null, isKnownWarning = () => true, blockers = [], planned = [], maxFiles = null } = {}) {
  const plannedFiles = planned.length;
  const { validation, preview, result, error, context = {} } = state;
  const files = filesOf(state, state.result ? [] : planned);
  const summary = preview?.analysisSummary || result?.analysisSummary || null;
  const acknowledged = [
    ...(preview?.periodSummary?.acknowledged || result?.period?.acknowledged || []),
    ...(preview?.branchMismatch || result?.branchMismatch || []),
  ];
  const periodWarnings = validation?.acknowledgements?.length ? validation.acknowledgements : acknowledged;
  const conflicts = (preview?.conflicts || error?.details?.conflicts || []);
  const detailErrors = Array.isArray(error?.details?.errors) ? error.details.errors : [];
  const missing = Array.isArray(error?.details?.missing) ? error.details.missing : [];
  const staleByApp = (preview?.stale || result?.stale || []).reduce((acc, item) => ({ ...acc, [item.row?.app]: (acc[item.row?.app] || 0) + 1 }), {});

  const appKeys = [...new Set([
    ...(state.result ? [] : state.apps || []),
    ...files.map((file) => file.app),
    ...(summary?.apps || []).map((item) => item.app),
    ...detailErrors.map((item) => item.app),
    ...missing.map((item) => item.app),
    ...blockers.map((item) => item.app),
  ].filter(Boolean))];

  const apps = appKeys.map((app) => {
    const appFiles = files.filter((file) => file.app === app);
    const appSummary = summary?.apps?.find((item) => item.app === app) || null;
    const errors = [
      ...appFiles.flatMap((file) => file.errors.map((item) => issue(item, { app, section: file.section, fileName: file.fileName }))),
      ...detailErrors.filter((item) => item.app === app).map((item) => issue(item, { fileName: item.fileName || appFiles.find((file) => file.section === item.section)?.fileName || null })),
      ...missing.filter((item) => item.app === app).map((item) => issue({ code: "MISSING_PART", app, fileName: item.fileName, fileVersion: item.fileVersion })),
      ...conflicts.filter((item) => (item.row?.app || null) === app).map((item) => issue({ code: item.code, app, protections: item.protections, reference: item.row?.orderId || item.row?.date || item.id })),
      ...blockers.filter((item) => item.app === app && !item.warningOnly).map(blockerIssue),
    ];
    const warnings = [
      ...blockers.filter((item) => item.app === app && item.warningOnly).map(blockerIssue),
      ...appFiles.flatMap((file) => file.warnings.filter((warning) => knownWarning(warning, isKnownWarning)).map((warning) => issue(warning, { app, section: file.section, fileName: file.fileName }))),
    ];
    const period = periodWarnings.filter((item) => item.app === app);
    const kinds = appSummary?.kinds || {};
    const mainKind = (summary?.mode || context.mode) === "M" ? "monthly" : "daily";
    const actions = Object.values(kinds).reduce((acc, kind) => {
      for (const [action, value] of Object.entries(kind.actions || {})) acc[action] = (acc[action] || 0) + value;
      return acc;
    }, {});
    const rowsKnown = appFiles.length > 0 && appFiles.every((file) => typeof file.sourceRows === "number");
    return {
      app,
      status: errors.length ? "error" : warnings.length || period.length ? "warning" : "ready",
      sections: [...new Set(appFiles.map((file) => file.section).filter(Boolean))],
      files: appFiles,
      sourceRows: appSummary?.sourceRows ?? (rowsKnown ? appFiles.reduce((sum, file) => sum + file.sourceRows, 0) : null),
      accounting: appSummary?.accounting || null,
      branches: appSummary ? appSummary.branches : null,
      errors,
      warnings,
      periodWarnings: period,
      main: kinds[mainKind] || null,
      compensation: kinds.compensation || null,
      database: appSummary ? {
        insert: actions.insert || 0,
        update: actions.update || 0,
        unchanged: actions.unchanged || 0,
        stale: staleByApp[app] || 0,
        conflict: conflicts.filter((item) => item.row?.app === app).length,
        added: appFiles.filter((file) => file.operation === "add").length,
        replaced: appFiles.filter((file) => file.operation === "replace" && !file.noop).length,
      } : null,
    };
  });

  // Errors that belong to no application (request-level failures).
  const general = [
    ...(maxFiles && plannedFiles > maxFiles ? [{ code: "TOO_MANY_FILES", count: maxFiles }] : []),
    ...detailErrors.filter((item) => !item.app),
    ...conflicts.filter((item) => !item.row?.app).map((item) => issue({ code: item.code, protections: item.protections, reference: item.id })),
  ];
  const withFiles = apps.filter((item) => item.files.length || item.errors.length);
  const errorCount = apps.reduce((sum, item) => sum + item.errors.length, 0) + general.length;
  const warningCount = apps.reduce((sum, item) => sum + item.warnings.length + item.periodWarnings.length, 0);
  const rowsKnown = files.length > 0 && files.every((file) => typeof file.sourceRows === "number");
  const blocked = (preview?.conflictCount || 0) > 0 || validation?.status === "rejected";
  const ackCodes = [...new Set((validation?.acknowledgements || []).map((item) => item.code))];
  const acknowledgedAll = ackCodes.every((code) => (state.acknowledgements || []).includes(code));
  const crossStore = (validation?.acknowledgements || []).filter((item) => item.code === CROSS_STORE);
  const dailyPeriod = (validation?.acknowledgements || []).filter((item) => item.code === DAILY_PERIOD);
  return {
    context: { storeName: summary?.store?.name || storeName, period: summary?.period || (context.year && context.month ? { year: Number(context.year), month: Number(context.month) } : null), mode: summary?.mode || context.mode },
    totals: {
      apps: withFiles.length,
      files: files.length || plannedFiles,
      sourceRows: summary?.totals?.sourceRows ?? (rowsKnown ? files.reduce((sum, file) => sum + file.sourceRows, 0) : null),
      branches: summary ? summary.totals.branches : null,
      warnings: warningCount,
      errors: errorCount,
      ready: withFiles.filter((item) => item.status === "ready").length,
      withWarnings: withFiles.filter((item) => item.status === "warning").length,
      withErrors: withFiles.filter((item) => item.status === "error").length,
      metrics: summary?.totals?.kinds?.[(summary.mode === "M" ? "monthly" : "daily")] || null,
      compensation: summary?.totals?.kinds?.compensation || null,
    },
    apps,
    general,
    acknowledged,
    // Codes the acknowledgement sends; the period and cross-store warnings each need their own tick.
    ackCodes,
    hasPeriodAck: ackCodes.includes(SOURCE_PERIOD),
    dailyExcludedRows: dailyPeriod.length ? dailyPeriod.reduce((sum, item) => sum + (item.excludedRows || 0), 0) : null,
    crossStoreRows: crossStore.length ? crossStore.reduce((sum, item) => sum + (item.affectedRows || 0), 0) : null,
    rejected: validation?.status === "rejected",
    // The checks found confirmable warnings: the administrator must acknowledge them (server token).
    needsConfirmation: Boolean(validation && validation.status !== "rejected" && validation.requiresConfirmation),
    canSave: Boolean(preview?.planFingerprint) && !blocked && validation?.status !== "rejected"
      && (!validation?.requiresConfirmation || Boolean(state.confirmationToken)) && acknowledgedAll,
    replayed: Boolean(result?.replayed),
    cleanupPending: Boolean(result?.cleanupPending),
  };
}
